import * as React from 'react'
// libs
import { Helmet } from 'react-helmet-async'
import { isMobile } from 'react-device-detect'
import { useRouter } from 'hooks/useRouter'
import { useSelector } from 'react-redux'
// components
import MainLayoutContainer from 'containers/MainLayout/index'
// view components
import { CloseRiskViewButton } from 'components/CTAs'
// context
import { RiskRegisterContext } from 'containers/Risks/RisksRegister/layers'
// constants
import { Modals } from 'constants/modals'
// hooks
import { useCurrentUser } from 'hooks/index'
// themes
import { LayoutContainerInnerWrapper } from 'themes/views/layouts'
// views
import { Content, Sidebar } from './views'
// view components
import RiskView from 'containers/Risks/RiskView'
import AiChatModal from 'components/Modals/AiChatModal'
import RiskViewsList from 'components/PageComponents/Risks/Register/components/RiskViewsList'
import RiskTemplatesList from 'components/PageComponents/Risks/Register/components/RiskTemplatesList'
import AddRiskViewDrawer from 'components/Modals/RiskViewModal'
import ConfirmDeleteModal from 'components/Modals/ConfirmDeleteModal'
// store
import { riskTemplatesLoadingSelector } from 'store/reducers/riskTemplates'
import { riskViewsLoadingSelector, riskViewsSelector } from 'store/reducers/riskViews'
// utils
import { get } from 'lodash'

const RisksRegister: React.FC = () => {
  const router = useRouter()

  const {
    permissionsFromSecurityGroups: { manageRiskTemplates }
  } = useCurrentUser()

  const { riskView, selectRiskView } = React.useContext(RiskRegisterContext)

  const riskViews = useSelector(riskViewsSelector)
  const riskViewsLoading = useSelector(riskViewsLoadingSelector)
  const riskTemplatesLoading = useSelector(riskTemplatesLoadingSelector)

  const isLoading = riskViewsLoading || riskTemplatesLoading
  const canViewLists = !!riskViews.length || manageRiskTemplates

  // select initial riskView
  React.useEffect(() => {
    if (!riskView && riskViews.length) {
      const storedRiskView = localStorage.getItem('riskView')

      if (router.query.riskViewId) {
        const prevRiskView = riskViews.find(({ id }) => id === router.query.riskViewId)

        if (prevRiskView) {
          selectRiskView(prevRiskView)
        }

        return
      }

      if (storedRiskView) {
        const prevRiskView = riskViews.find(({ id }) => id === storedRiskView)

        if (prevRiskView) {
          selectRiskView(prevRiskView)
        }
      } else if (!isMobile) {
        selectRiskView(riskViews[0])

        localStorage.setItem('riskView', riskViews[0].id)
      }
    }
  }, [riskViews.length, riskView])

  return (
    <>
      <Helmet>
        <title>Skefto - Forms</title>
      </Helmet>
      <MainLayoutContainer isLoading={isLoading} CTA={<CloseRiskViewButton />}>
        <LayoutContainerInnerWrapper offset={20}>
          <Content>
            {canViewLists && (
              <>
                <Sidebar $isViewSelected={!!riskView} options={{ suppressScrollX: true }}>
                  <RiskViewsList />
                  {manageRiskTemplates && <RiskTemplatesList />}
                </Sidebar>

                {riskView && <RiskView />}
              </>
            )}
          </Content>

          <AiChatModal />
          <AddRiskViewDrawer />
          <ConfirmDeleteModal modalType={Modals.CONFIRM_DELETE_CHAT_CONVERSATION_MODAL} header={`Delete Conversation`}>
            {`Are you sure you want to delete this conversation?`}
          </ConfirmDeleteModal>
          <ConfirmDeleteModal
            header={`Delete ${get(riskView, 'name')}`}
            modalType={Modals.CONFIRM_DELETE_RISK_VIEW_MODAL}>
            Are you sure you want to delete this view?
          </ConfirmDeleteModal>
        </LayoutContainerInnerWrapper>
      </MainLayoutContainer>
    </>
  )
}

export default RisksRegister
